import type { CheckItem, DatabaseFile, PendingItemVideo } from '../types';
import type { GitHubService } from './GitHubService';
import { mp4FastStartBytes } from '../utils/mp4FastStart';
import { videoRepoPath } from '../utils/itemVideo';
import { logThumbDebug } from '../utils/mediaDebugLog';
import {
  averageLuminanceFromBlob,
  isAcceptableThumbLuminance,
  videoThumbnailFilename,
  videoThumbnailRepoPath,
} from '../utils/videoThumbnail';
import { committedVideoRepoFilename } from '../utils/videoFilenameEdit';

export function pendingVideoThumbError(filename: string, detail: string): Error {
  return new Error(`Video ${filename}: ${detail}. Re-attach the clip or remove it before saving.`);
}

/** Pending clips must already have a valid thumb from attach-time capture. */
export async function requirePendingVideoThumb(pending: PendingItemVideo): Promise<Blob> {
  if (!pending.thumbBlob) {
    throw pendingVideoThumbError(pending.filename, 'no thumbnail preview');
  }
  const lum = await averageLuminanceFromBlob(pending.thumbBlob);
  if (!isAcceptableThumbLuminance(lum)) {
    throw pendingVideoThumbError(pending.filename, `thumbnail preview invalid (luminance ${lum ?? 'null'})`);
  }
  return pending.thumbBlob;
}

export function assertAllPendingVideosHaveThumbs(file: DatabaseFile): void {
  for (const section of file.sections) {
    for (const item of section.items) {
      for (const pending of item.pendingVideo) {
        if (!pending.thumbBlob) {
          throw pendingVideoThumbError(pending.filename, 'no thumbnail preview');
        }
      }
    }
  }
}

async function renameCommittedVideoOnRepo(
  github: GitHubService,
  mdPath: string,
  from: string,
  to: string
): Promise<void> {
  const oldVideo = videoRepoPath(mdPath, from);
  const newVideo = videoRepoPath(mdPath, to);
  const oldThumb = videoThumbnailRepoPath(mdPath, from);
  const newThumb = videoThumbnailRepoPath(mdPath, to);

  const videoSha = await github.getBlobSha(oldVideo);
  if (!videoSha) return;

  const { bytes } = await github.readBinary(oldVideo);
  const destVideoSha = await github.getBlobSha(newVideo);
  await github.writeBinary(newVideo, bytes, destVideoSha ?? '', `App: rename video ${from} → ${to}`);

  const thumbSha = await github.getBlobSha(oldThumb);
  if (thumbSha) {
    const { bytes: thumbBytes } = await github.readBinary(oldThumb);
    const destThumbSha = await github.getBlobSha(newThumb);
    await github.writeBinary(
      newThumb,
      thumbBytes,
      destThumbSha ?? '',
      `App: rename video thumbnail ${videoThumbnailFilename(from)} → ${videoThumbnailFilename(to)}`
    );
    await github.deleteFile(
      oldThumb,
      thumbSha,
      `App: rename video thumbnail ${videoThumbnailFilename(from)} → ${videoThumbnailFilename(to)}`
    );
  }

  await github.deleteFile(oldVideo, videoSha, `App: rename video ${from} → ${to}`);
}

export async function syncItemVideoForFile(github: GitHubService, file: DatabaseFile): Promise<void> {
  for (const path of file.orphanedVideoPaths) {
    const sha = await github.getBlobSha(path);
    if (sha) await github.deleteFile(path, sha, `App: remove video ${path.split('/').pop()}`);
  }
  file.orphanedVideoPaths = [];

  for (const section of file.sections) {
    for (const item of section.items) {
      for (const rename of item.videoRenames ?? []) {
        if (rename.from === rename.to) continue;
        await renameCommittedVideoOnRepo(github, file.path, rename.from, rename.to);
      }
      item.videoRenames = [];

      for (const filename of item.videoToDelete) {
        const path = videoRepoPath(file.path, filename);
        const sha = await github.getBlobSha(path);
        if (sha) await github.deleteFile(path, sha, `App: remove video ${filename}`);
        const thumbPath = videoThumbnailRepoPath(file.path, filename);
        const thumbSha = await github.getBlobSha(thumbPath);
        if (thumbSha) {
          await github.deleteFile(
            thumbPath,
            thumbSha,
            `App: remove video thumbnail ${videoThumbnailFilename(filename)}`
          );
        }
        item.video = item.video.filter(f => f !== filename);
      }
      item.videoToDelete = [];

      for (const pending of item.pendingVideo) {
        const videoPath = videoRepoPath(file.path, pending.filename);
        const thumbPath = videoThumbnailRepoPath(file.path, pending.filename);
        const thumbBlob = await requirePendingVideoThumb(pending);
        const lum = await averageLuminanceFromBlob(thumbBlob);

        let bytes = new Uint8Array(await pending.file.arrayBuffer());
        const remuxed = mp4FastStartBytes(bytes);
        if (remuxed) bytes = new Uint8Array(remuxed);

        let videoWritten = false;
        try {
          await github.writeBinary(thumbPath, await thumbBlob.arrayBuffer(), '', `App: add video thumbnail ${videoThumbnailFilename(pending.filename)}`);
          logThumbDebug(videoPath, 'thumb-save-ok', `Uploaded ${videoThumbnailFilename(pending.filename)} (${thumbBlob.size} bytes)`, true, {
            luminance: lum,
            thumbPath,
          });

          await github.writeBinary(videoPath, bytes, '', `App: add video ${pending.filename}`);
          videoWritten = true;
        } catch (err) {
          const thumbSha = await github.getBlobSha(thumbPath);
          if (thumbSha) {
            await github.deleteFile(
              thumbPath,
              thumbSha,
              `App: rollback video thumbnail ${videoThumbnailFilename(pending.filename)}`
            );
          }
          if (videoWritten) {
            const videoSha = await github.getBlobSha(videoPath);
            if (videoSha) {
              await github.deleteFile(videoPath, videoSha, `App: rollback video ${pending.filename}`);
            }
          }
          logThumbDebug(
            videoPath,
            'thumb-save-fail',
            err instanceof Error ? err.message : 'video/thumbnail upload failed',
            false,
            { thumbPath }
          );
          throw err instanceof Error ? err : new Error('Video upload failed');
        }

        if (!item.video.includes(pending.filename)) item.video.push(pending.filename);
      }
      item.pendingVideo = [];
    }
  }
}

export function queueOrphanedVideoForItem(file: DatabaseFile, item: CheckItem): void {
  for (const filename of item.video) {
    const repoName = committedVideoRepoFilename(item, filename);
    file.orphanedVideoPaths.push(videoRepoPath(file.path, repoName));
    file.orphanedVideoPaths.push(videoThumbnailRepoPath(file.path, repoName));
  }
}
